import React, { useEffect, useRef, useState } from "react";

// DocuSign-style "Type or Draw" capture block. No dependencies beyond React: it renders its own
// PNG (typed name onto an offscreen canvas, or the drawn strokes directly) and hands it to the
// caller via onAdopt. The caller (SignatureOnFile) does the upload and owns any error toast.

const SCRIPT_FONT = '"Brush Script MT", "Segoe Script", cursive';
const INK_COLOR = "#0F172A";
const CANVAS_W = 600;
const CANVAS_H = 200;

function normalizeName(s) {
  return (s || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function renderTypedNamePng(name) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W;
    canvas.height = 160;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = INK_COLOR;
    ctx.font = `56px ${SCRIPT_FONT}`;
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.fillText(name, 20, 105);
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Could not render the typed signature"));
    }, "image/png");
  });
}

export default function SignatureBlock({ legalName, onAdopt }) {
  const [tab, setTab] = useState("type"); // "type" | "draw"
  const [typedValue, setTypedValue] = useState("");
  const [hasDrawn, setHasDrawn] = useState(false);
  const [busy, setBusy] = useState(false);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [tab]);

  const typedMatches = !legalName || normalizeName(typedValue) === normalizeName(legalName);
  const typedValid = typedValue.trim().length > 0 && typedMatches;
  const valid = tab === "type" ? typedValid : hasDrawn;

  const posFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    canvas.setPointerCapture?.(e.pointerId);
    drawingRef.current = true;
    lastPointRef.current = posFromEvent(e);
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = posFromEvent(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = INK_COLOR;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPointRef.current = pos;
    if (!hasDrawn) setHasDrawn(true);
  };

  const handlePointerUp = (e) => {
    e.preventDefault();
    drawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const adopt = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      let method, typedName, blob;
      if (tab === "type") {
        method = "typed";
        typedName = typedValue.trim();
        blob = await renderTypedNamePng(typedName);
      } else {
        method = "drawn";
        typedName = (legalName || typedValue).trim() || "Signature";
        blob = await new Promise((resolve, reject) => {
          canvasRef.current.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not read the drawn signature"))), "image/png");
        });
      }
      await onAdopt?.({ method, typedName, blob });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="signature-block">
      <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-md">
        <button
          type="button"
          onClick={() => setTab("type")}
          className={`text-sm py-2 rounded ${tab === "type" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
          style={{ minHeight: 44 }}
          data-testid="sig-tab-type"
        >
          Type
        </button>
        <button
          type="button"
          onClick={() => setTab("draw")}
          className={`text-sm py-2 rounded ${tab === "draw" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
          style={{ minHeight: 44 }}
          data-testid="sig-tab-draw"
        >
          Draw
        </button>
      </div>

      {tab === "type" && (
        <div className="mt-3">
          <label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Full legal name</label>
          <input
            type="text"
            value={typedValue}
            onChange={(e) => setTypedValue(e.target.value)}
            placeholder="Type your full name"
            className="mt-1 w-full text-sm px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
            style={{ minHeight: 44 }}
            data-testid="sig-typed-name"
          />
          {legalName && !typedMatches && (
            <div className="text-[11px] text-amber-700 mt-1" data-testid="sig-name-hint">
              Type your name exactly as it appears on your license: {legalName}
            </div>
          )}

          <div className="border border-slate-300 rounded-md bg-white px-4 pt-6 pb-2 mt-3">
            <div className="min-h-[48px] flex items-end pb-1" style={{ fontFamily: SCRIPT_FONT, fontSize: 34, color: INK_COLOR }}>
              {typedValue.trim() || <span className="text-slate-300 text-sm" style={{ fontFamily: "inherit", fontSize: 14 }}>Your signature preview</span>}
            </div>
            <div className="border-t border-slate-400" />
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">Sign here</div>
          </div>
        </div>
      )}

      {tab === "draw" && (
        <div className="mt-3">
          <div className="relative border border-slate-300 rounded-md bg-white overflow-hidden" style={{ touchAction: "none" }}>
            <canvas
              ref={canvasRef}
              width={CANVAS_W}
              height={CANVAS_H}
              className="block w-full h-auto"
              style={{ touchAction: "none" }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              data-testid="sig-canvas"
            />
            <div className="pointer-events-none absolute left-4 right-4" style={{ bottom: 28 }}>
              <div className="border-b border-dashed border-slate-300" />
            </div>
            <div className="pointer-events-none absolute left-4 text-[10px] uppercase tracking-wider text-slate-400" style={{ bottom: 8 }}>
              Sign here
            </div>
          </div>
          <button
            type="button"
            onClick={clearCanvas}
            className="mt-2 text-xs text-slate-500 hover:text-slate-800 px-3 py-2"
            style={{ minHeight: 44 }}
            data-testid="sig-clear"
          >
            Clear
          </button>
        </div>
      )}

      <div className="text-[11px] text-slate-500 mt-3">
        By selecting Adopt and sign, I agree that this electronic signature is the legal equivalent of my handwritten signature.
      </div>

      <button
        type="button"
        onClick={adopt}
        disabled={!valid || busy}
        className="mt-3 w-full bg-[#0A192F] hover:bg-[#112240] disabled:opacity-50 text-white text-sm rounded-md font-semibold"
        style={{ minHeight: 44 }}
        data-testid="sig-adopt"
      >
        {busy ? "Saving…" : "Adopt and sign"}
      </button>
    </div>
  );
}
